import React, { useRef, useState } from 'react';
import SwiperCore, { EffectFade } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import useAutoplay from '../hooks/useAutoplay';

SwiperCore.use([EffectFade]);

/** Autoplay de la galerie salon — 4500 ms, valeur de la maquette. */
const AUTOPLAY_DELAY = 4500;

/** Durée du fondu — reprend `.salon-diap-track img { transition: opacity .4s }` de la maquette. */
const FADE_SPEED = 400;

/**
 * `.salon-diap` — galerie du salon : fondu enchaîné, flèches, points, compteur.
 *
 * Swiper (déjà une dépendance du repo, utilisée par les richfields `common` et
 * par 2025_VOG) porte désormais le geste tactile — la version précédente
 * n'avait aucun handler de swipe. `effect="fade"` reprend exactement le
 * fondu-enchaîné CSS déjà ISO (mêmes 400 ms), navigation/pagination/compteur
 * restent nos propres éléments, pilotés via l'instance Swiper plutôt que par
 * les widgets par défaut — pour ne rien changer au rendu déjà validé.
 *
 * Corrections conservées :
 *  - piège n°8 : chaque image porte sa propre description, générée à partir
 *    du nom du salon (jamais « photo 1 sur 5 » figé) ;
 *  - le compteur et les points sont dérivés de `photos.length`, jamais écrits
 *    en dur ;
 *  - l'autoplay se met en pause au survol/focus comme dans la maquette, et ne
 *    démarre pas du tout si `prefers-reduced-motion` est demandé (`useAutoplay`
 *    inchangé, son tick pilote juste `swiper.slideNext()`).
 */
const SalonGallery = ({ photos = [], salonName = '' }) => {
  const [current, setCurrent] = useState(0);
  const containerRef = useRef(null);
  const swiperRef = useRef(null);
  const total = photos.length;

  const goTo = (index) => swiperRef.current && swiperRef.current.slideToLoop(((index % total) + total) % total);
  useAutoplay(() => swiperRef.current && swiperRef.current.slideNext(), AUTOPLAY_DELAY, containerRef, total > 1);

  if (!total) return null;

  return (
    <div className="salon-diap" id="salonDiap" ref={containerRef}>
      <Swiper
        className="salon-diap-track"
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={FADE_SPEED}
        loop={total > 1}
        allowTouchMove={total > 1}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
      >
        {photos.map((photo, index) => (
          <SwiperSlide key={photo.src}>
            <img
              src={photo.src}
              alt={photo.alt ? `${salonName} — ${photo.alt}` : `${salonName} — photo ${index + 1} sur ${total}`}
              width="760"
              height="507"
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : undefined}
              aria-hidden={index === current ? undefined : 'true'}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {total > 1 && (
        <>
          <div className="salon-diap-counter" aria-live="polite">
            {current + 1} / {total}
          </div>
          <div className="salon-diap-arrows">
            <button type="button" aria-label="Photo précédente" onClick={() => goTo(current - 1)}>
              ←
            </button>
            <button type="button" aria-label="Photo suivante" onClick={() => goTo(current + 1)}>
              →
            </button>
          </div>
          <div className="salon-diap-dots">
            {photos.map((photo, index) => (
              <button
                key={photo.src}
                type="button"
                className={index === current ? 'active' : undefined}
                aria-label={`Photo ${index + 1}`}
                aria-current={index === current ? 'true' : undefined}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SalonGallery;
